import { useAuthStore } from "@kaplan-labs/up-auth-api-client";
import { ErrorBoundary } from "react-error-boundary";

import { ProfileType, logError, useProfile, useTheme } from "@up/data";
import { Auth, BoundaryFallback, BrowserOnly } from "@up/ui";

import { ProfileNav } from "./profile-nav";

import type { ErrorInfo, PropsWithChildren, ReactNode } from "react";

const logBoundaryError = (error: unknown, info: ErrorInfo) => {
  logError({
    action: "PROFILE_BOUNDARY_ERROR",
    error,
    context: { up_stack: info.componentStack },
  });
};

function Go({
  children,
  title,
}: PropsWithChildren<{
  title: ReactNode;
}>) {
  const auth = useAuthStore();
  const theme = useTheme();
  const profile = useProfile();

  if (!auth.currentUser) {
    return (
      <div className="mx-auto max-w-2xl md:px-8 animation-fade-in">
        <h1 className={theme.heading1Class}>Profile</h1>
        <div className="h-8" aria-hidden />
        <Auth />
      </div>
    );
  }

  return (
    <>
      <h1 className={`${theme.heading1Class} text-center md:text-left`}>
        {title}
      </h1>
      <div className="h-2 md:h-8" aria-hidden />
      <div className="profile-layout">
        <div className="profile-nav">
          <ProfileNav />
        </div>

        <div className="profile-sidebar">
          <div className="mb-1">
            <h2 className={theme.heading2Class}>Support</h2>
          </div>
          <p>
            <a
              className="underline"
              href={`mailto:${theme.supportEmail}`}
              style={{ overflowWrap: "break-word" }}
            >
              {theme.supportEmail}
            </a>
          </p>{" "}
          {profile.data?.type === ProfileType.Student ? (
            <>
              <div className="h-8" aria-hidden />
              <div className="mb-1">
                <h2 className={theme.heading2Class}>Canvas</h2>
              </div>
              <p>
                <a
                  className="underline"
                  href={theme.lmsURL}
                  target="_blank"
                  style={{ overflowWrap: "break-word" }}
                  rel="noreferrer"
                >
                  Launch Canvas
                </a>
              </p>
            </>
          ) : null}
        </div>

        <div className="profile-content">{children}</div>
      </div>
    </>
  );
}

export function ProfileLayout({
  children,
  title = "Profile",
}: PropsWithChildren<{
  title?: ReactNode;
}>) {
  return (
    <BrowserOnly>
      <ErrorBoundary
        FallbackComponent={BoundaryFallback}
        onError={logBoundaryError}
      >
        <Go title={title}>{children}</Go>
      </ErrorBoundary>
    </BrowserOnly>
  );
}
